/**
 * A class for handling asynchronous image uploads using Kendo UI Upload.
 *
 * @class
 * @name AsyncImageUpload
 * @param {Object} options - The options for configuring the image upload.
 * @param {string} options.kendoInputSelector - The CSS selector for the Kendo UI Upload input element.
 * @param {string} options.valueInputSelector - The CSS selector for the hidden input element that stores the uploaded image paths.
 */
export class AsyncImageUpload {

    /**
     * Returns the Kendo UI Upload input element based on the provided CSS selector.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @type {HTMLElement}
     */
    get kendoInputEl() {
        return document.querySelector(this.kendoInputSelector)
    }

    /**
     * Returns the hidden input element that stores the uploaded image paths
     * based on the provided CSS selector.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @type {HTMLElement}
     */
    get valueInputEl() {
        return document.querySelector(this.valueInputSelector)
    }

    /**
     * Creates an instance of AsyncImageUpload.
     *
     * You can access the instance of AsyncImageUpload using the
     * `asyncImageUploader` property of the hidden input element that stores
     * the uploaded image paths.
     *
     * @constructor
     * @memberof AsyncImageUpload
     * @param {Object} options - The options for configuring the image upload.
     * @param {string} options.kendoInputSelector - The CSS selector for the Kendo UI Upload input element.
     * @param {string} options.valueInputSelector - The CSS selector for the hidden input element that stores the uploaded image paths.
     */
    constructor({
        kendoInputSelector,
        valueInputSelector
    }) {
        this.kendoInputSelector = kendoInputSelector
        this.valueInputSelector = valueInputSelector

        const kendoInputDataset = this.kendoInputEl.dataset
        this.captchaErrorMessage = kendoInputDataset.captchaErrorMessage
        this.genericErrorMessage = kendoInputDataset.genericErrorMessage
        this.captchaUrl = kendoInputDataset.captchaUrl
        this.uploadUrl = kendoInputDataset.uploadUrl
        this.csrfToken = this.kendoInputEl.closest('form').querySelector('[name="_csrfToken"]').value

        this.isUploading = false
        this.isCaptchaValidated = false
        this.kendoUploadInstance = null

        this.onSelect = this.onSelect.bind(this)
        this.onUpload = this.onUpload.bind(this)
        this.onSuccess = this.onSuccess.bind(this)
        this.onError = this.onError.bind(this)
        this.onComplete = this.onComplete.bind(this)

        this.initializeKendo()

        this.valueInputEl.asyncImageUploader = this
    }

    /**
     * Initializes the Kendo UI Upload widget with the provided options and sets up event handlers.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name initializeKendo
     */
    initializeKendo() {
        $(this.kendoInputEl).kendoUpload({
            async: {
                saveUrl: this.uploadUrl,
                autoUpload: false
            },
            validation: {
                allowedExtensions: ['.jpg', '.jpeg', '.png'],
                maxFileSize: 10 * 1024 * 1024
            },
            select: this.onSelect,
            upload: this.onUpload,
            success: this.onSuccess,
            error: this.onError,
            complete: this.onComplete
        })

        this.kendoUploadInstance = $(this.kendoInputEl).data("kendoUpload")
    }

    /**
     * Shows an alert message using Tippy.js.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name showAlert
     * @param {Object} options - The options for configuring the alert message.
     * @param {string} options.message - The message to display in the alert.
     * @param {string} options.theme - The theme to use for the alert.
     * @param {number} [options.duration=15000] - The duration in milliseconds for the alert to be displayed.
     */
    showAlert({
        message,
        theme,
        duration = 15 * 1000
    }) {
        Eshop.tippyAlert(this.kendoInputEl.closest('.c-form__item--file'), message, {
            theme: theme,
            zIndex: 1060
        }, duration);
    }

    /**
     * Adds the provided image path to the hidden input element that stores the
     * uploaded image paths.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name addImagePathToValueInput
     * @param {string} image - The image path to add to the hidden input element.
     */
    addImagePathToValueInput(image) {
        const valueInputValue = this.valueInputEl.value

        let images = []

        try {
            images = JSON.parse(valueInputValue)
        } catch (error) {
        }

        images.push(image)

        this.valueInputEl.value = JSON.stringify(images)
    }

    /**
     * If the captcha check succeeds, initiates the upload process.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name onSelect
     * @returns {boolean} - Returns true if the upload process is initiated, false otherwise.
     */
    async onSelect() {
        this.isUploading = true

        const captchaResult = await this.checkCaptcha()

        if (captchaResult !== true) {
            this.showAlert({
                message: captchaResult,
                theme: 'error'
            })

            this.isUploading = false
            return false
        }

        this.kendoUploadInstance.upload()

        return true
    }

    /**
     * Sets the `isUploading` flag to true and adds the CSRF token to the upload
     * event data.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name onUpload
     * @param {Object} event - The upload event object.
     * @param {Object} event.data - The data object to be sent with the upload request.
     * @param {string} event.data._csrfToken - The CSRF token to be sent with the upload request.
     */
    onUpload(event) {
        this.isUploading = true

        event.data = {
            _csrfToken: this.csrfToken
        }
    }

    /**
     * Handles the successful upload of an image. If the upload was successful,
     * adds the image path to the hidden input element that stores the uploaded
     * image paths. If the upload was not successful, shows an error message
     * using Tippy.js.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name onSuccess
     * @param {Object} data - The data object returned from the server after a successful upload.
     * @param {Object} data.response - The response object returned from the server after a successful upload.
     * @param {string} data.response.success - The image path returned from the server after a successful upload.
     * @param {string} [data.response.message] - The error message returned from the server after an unsuccessful upload.
     */
    onSuccess(data) {
        if (data.response.success) {
            this.addImagePathToValueInput(data.response.success);
        } else {
            console.error(data);

            this.showAlert({
                message: data.response.message || this.genericErrorMessage,
                theme: 'error'
            })
        }
    }

    /**
     * Handles errors that occur during the image upload process. Checks the
     * captcha and shows an error message using Tippy.js.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name onError
     * @param {Object} data - The data object returned from the server after an unsuccessful upload.
     * @param {Object} data.XMLHttpRequest - The XMLHttpRequest object returned from the server after an unsuccessful upload.
     * @param {string} [data.response.message] - The error message returned from the server after an unsuccessful upload.
     */
    onError(data) {
        this.checkCaptcha(true);

        let message = this.genericErrorMessage

        try {
            message += ` - ${data.XMLHttpRequest.status} ${data.XMLHttpRequest.statusText}`
        } catch (error) {
        }

        this.showAlert({
            message: message,
            theme: 'error'
        })
    }

    /**
     * Sets the `isUploading` flag to false when the upload process is complete.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name onComplete
     */
    onComplete() {
        this.isUploading = false;
    }

    /**
     * Checks the captcha and sets the `isCaptchaValidated` flag to true if the
     * captcha is valid.
     * If the captcha is not valid, shows an error message using Tippy.js.
     *
     * @memberof AsyncImageUpload
     * @instance
     * @function
     * @name checkCaptcha
     * @param {boolean} [forceRevalidation=false] - Whether to force revalidation of the captcha.
     * @returns {boolean|string} - Returns true if the captcha is valid, otherwise returns an error message.
     */
    async checkCaptcha(forceRevalidation = false) {
        if (!forceRevalidation && this.isCaptchaValidated) {
            return true;
        }
        this.isCaptchaValidated = false;

        const captchaToken = await getRecaptchaToken('async_image_upload');

        try {
            const response = await fetch(this.captchaUrl, {
                method: 'POST',
                body: JSON.stringify({ captchaToken }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-Csrf-Token': this.csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                }
            });

            const data = await response.json();

            if (data.status === true) {
                this.isCaptchaValidated = true;

                return true;
            } else {
                console.error(data);

                return data.message || this.captchaErrorMessage;
            }
        } catch (error) {
            console.error(error);

            return error.message || this.genericErrorMessage;
        }
    }
}
